import { FORMULATION_FILTER_ID, ProductFormulations } from '@/constants/products';
import { useAppStore } from '@/state-management/store';
import { calculateDispenseAmount, sumDispenseAmounts } from '@/utils/dosageLimit/calculateDispenseAmount';
import { DISPENSE_PERIOD_DAYS } from '@/utils/dosageLimit/constants';
import { Logger } from '@/utils/logger';
import { useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

// TODO: Hardcoded for now; will fetch from API as part of B2C-7592 Epic
const FLOWER_DISPENSE_LIMIT_GRAMS = 100;

/** `stateCode` is free-form and may be absent, and without a limit the comparison below stops firing entirely. */
const UNKNOWN_STATE_DISPENSE_LIMIT_GRAMS = FLOWER_DISPENSE_LIMIT_GRAMS;

const FlowerDispenseLimits = new Map([
  ['ACT', FLOWER_DISPENSE_LIMIT_GRAMS],
  ['NSW', FLOWER_DISPENSE_LIMIT_GRAMS],
  ['NT', FLOWER_DISPENSE_LIMIT_GRAMS],
  ['QLD', FLOWER_DISPENSE_LIMIT_GRAMS],
  ['SA', FLOWER_DISPENSE_LIMIT_GRAMS],
  ['TAS', FLOWER_DISPENSE_LIMIT_GRAMS],
  ['VIC', FLOWER_DISPENSE_LIMIT_GRAMS],
  ['WA', FLOWER_DISPENSE_LIMIT_GRAMS],
]);

let lastState = { isExceeded: false, totalGrams: 0 };
const logger = new Logger('usePrescriptionDispenseLimitExceeded.ts');

/**
 * Zustand selector hook that checks if the patient’s "Herb, Dried" (Flower) prescriptions
 * exceed the state-allocated dispense limit. With dispense limits on, each item counts its calculated dispense amount:
 * one 30-day window, whatever its repeats. Otherwise it counts the whole fills its interval allows in 30 days, no more
 * than its repeats allow.
 * @returns boolean
 */
export const usePrescriptionDispenseLimitExceeded = () => {
  const { stateCode, medicationPadItems, enableDispenseLimits } = useAppStore(
    useShallow((state) => ({
      stateCode: state.patient.basicInfo.stateCode,
      medicationPadItems: state.prescriptionAssistant.prescriptionPad.medicationPadItems,
      enableDispenseLimits: state.prescriptionAssistant.prescriptionPadRules.enableDispenseLimits,
    })),
  );

  return useMemo(() => {
    const stateGramLimit = FlowerDispenseLimits.get(stateCode || '') ?? UNKNOWN_STATE_DISPENSE_LIMIT_GRAMS;
    const totalGrams = sumDispenseAmounts(
      medicationPadItems.map((medicationPadItem) => {
        if (!medicationPadItem || !medicationPadItem.medicationPadItem) {
          return 0;
        }

        if (medicationPadItem?.filters?.get(FORMULATION_FILTER_ID) !== ProductFormulations.HERB_DRIED) {
          return 0;
        }

        // The amount the pad shows for the item, so the alert and the dispense limits never count a script differently
        if (enableDispenseLimits) {
          return calculateDispenseAmount(medicationPadItem.medicationPadItem) ?? 0;
        }

        const { interval, repeats, quantity, dispenseSize } = medicationPadItem.medicationPadItem;
        if (quantity === undefined || dispenseSize === undefined) {
          return 0;
        }

        const smallerSizeInterval = Math.floor(DISPENSE_PERIOD_DAYS / (interval ? interval : 1));
        const smallerSizeRepeats = (repeats ? repeats : 0) + 1;

        const smallerSize = dispenseSize * quantity * Math.min(smallerSizeInterval, smallerSizeRepeats);

        return smallerSize;
      }),
    );

    const isExceeded = totalGrams >= stateGramLimit;

    if (isExceeded && (!lastState.isExceeded || totalGrams !== lastState.totalGrams)) {
      logger.warn(`Dispense limit exceeded! ${totalGrams}g`);
    }
    lastState = { isExceeded, totalGrams };

    return {
      isExceeded,
      totalGrams,
    };
  }, [medicationPadItems, stateCode, enableDispenseLimits]);
};
